import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

export interface CustomerData {
  id: number;
  customer_id: string;
  company_name: string;
  contact_person: string;
  phone_number: string;
  email: string;
  address: string;
  city: string;
  postal_code: string;
  discount_percentage: number;
  total_credit_limit: number;
  account_balance: number;
  is_active: boolean;
  created_at: string;
}

export interface OrderItem {
  id: number;
  product: { id: number; name: string; sku: string; category?: string | null };
  quantity: number;
  original_quantity: number | null;
  unit_price: number;
  subtotal: number;
}

export interface OrderData {
  id: number;
  order_number: string;
  customer_name: string;
  status: string;
  order_date: string;
  delivery_date: string | null;
  delivered_at: string | null;
  total_amount: number;
  original_total_amount: number | null;
  notes: string;
  items: OrderItem[];
}

export interface PaymentData {
  id: number;
  amount: number;
  payment_method: string;
  payment_method_display: string;
  reference_number: string;
  notes: string;
  created_at: string;
  created_by: string;
}

export const statusColors: Record<string, { bg: string; text: string }> = {
  pending: { bg: '#fef3c7', text: '#92400e' },
  confirmed: { bg: '#dbeafe', text: '#1e40af' },
  processing: { bg: '#ede9fe', text: '#5b21b6' },
  shipped: { bg: '#e0f2fe', text: '#075985' },
  delivered: { bg: '#ecfdf5', text: '#065f46' },
  cancelled: { bg: '#fee2e2', text: '#991b1b' },
};

export function useCustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isExpired } = useAuth();
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<OrderData | null>(null);

  // Payment state
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: 'cash', reference_number: '', notes: '' });
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Check if user can view/manage financials
  const canViewFinancials = user?.is_company_owner || !user?.permissions || user?.permissions?.can_view_financials !== false;

  useEffect(() => {
    if (!id) return;
    const customerId = Number(id);
    setLoading(true);
    Promise.all([
      companyAPI.getCustomer(customerId),
      companyAPI.getCustomerOrders(customerId),
    ])
      .then(([customerRes, ordersRes]) => {
        setCustomer(customerRes.data);
        setOrders(ordersRes.data);
      })
      .catch(err => {
        console.error('Failed to load customer details:', err);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const fetchPayments = useCallback(async () => {
    if (!id || !canViewFinancials) return;
    setPaymentsLoading(true);
    try {
      const res = await companyAPI.getCustomerPayments(Number(id));
      setPayments(res.data.payments || []);
      // Update customer balance from payments response
      if (customer) {
        setCustomer(prev => prev ? { ...prev, account_balance: res.data.account_balance } : prev);
      }
    } catch {
      // silent fail for payments
    } finally {
      setPaymentsLoading(false);
    }
  }, [id, canViewFinancials, customer]);

  useEffect(() => {
    if (customer && canViewFinancials) {
      fetchPayments();
    }
  }, [customer?.id, canViewFinancials]);

  const handleAddPayment = async () => {
    setPaymentError('');
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      setPaymentError('Enter a valid amount greater than zero.');
      return;
    }
    setPaymentSaving(true);
    try {
      const res = await companyAPI.addCustomerPayment(Number(id), {
        amount,
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number,
        notes: paymentForm.notes,
      });
      // Add new payment to list
      setPayments(prev => [res.data, ...prev]);
      // Update customer balance
      setCustomer(prev => prev ? { ...prev, account_balance: res.data.account_balance } : prev);
      // Reset form and close modal
      setPaymentForm({ amount: '', payment_method: 'cash', reference_number: '', notes: '' });
      setShowPaymentModal(false);
    } catch (err: any) {
      setPaymentError(err?.response?.data?.error || 'Failed to add payment.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const stats = useMemo(() => {
    if (orders.length === 0) return null;
    const nonCancelled = orders.filter(o => o.status !== 'cancelled');
    const totalRevenue = nonCancelled.reduce((sum, o) => sum + o.total_amount, 0);
    const avgOrder = nonCancelled.length > 0 ? totalRevenue / nonCancelled.length : 0;
    const delivered = orders.filter(o => o.status === 'delivered').length;
    const cancelled = orders.filter(o => o.status === 'cancelled').length;
    const active = orders.filter(o => ['pending', 'confirmed', 'processing', 'shipped'].includes(o.status)).length;
    const allAmounts = nonCancelled.map(o => o.total_amount);
    const largest = allAmounts.length > 0 ? Math.max(...allAmounts) : 0;
    const smallest = allAmounts.length > 0 ? Math.min(...allAmounts) : 0;
    const dates = orders.map(o => new Date(o.order_date)).sort((a, b) => a.getTime() - b.getTime());
    return {
      totalRevenue, avgOrder, delivered, cancelled, active,
      largest, smallest,
      totalOrders: orders.length,
      activeOrders: nonCancelled.length,
      firstOrder: dates[0],
      lastOrder: dates[dates.length - 1],
    };
  }, [orders]);

  // Product breakdown stats
  const productStats = useMemo(() => {
    const nonCancelled = orders.filter(o => o.status !== 'cancelled');
    if (nonCancelled.length === 0) return null;

    // Aggregate by category
    const catMap: Record<string, number> = {};
    // Aggregate by product (top 3)
    const prodMap: Record<string, { name: string; qty: number; revenue: number }> = {};
    let totalItems = 0;

    nonCancelled.forEach(o => {
      o.items.forEach(item => {
        totalItems += item.quantity;
        const cat = item.product.category || 'Other';
        catMap[cat] = (catMap[cat] || 0) + item.quantity;
        const key = `${item.product.id}`;
        if (!prodMap[key]) prodMap[key] = { name: item.product.name, qty: 0, revenue: 0 };
        prodMap[key].qty += item.quantity;
        prodMap[key].revenue += item.subtotal;
      });
    });

    const byCategory = Object.entries(catMap)
      .map(([name, qty]) => ({ name, qty }))
      .sort((a, b) => b.qty - a.qty);

    const topProducts = Object.values(prodMap)
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 3);

    return { totalItems, byCategory, topProducts };
  }, [orders]);

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[month - 1]} ${day}, ${year}`;
  };

  const fmt = (n: number) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return {
    navigate,
    isExpired,
    customer,
    orders,
    loading,
    selectedOrder,
    setSelectedOrder,
    stats,
    productStats,
    formatDate,
    fmt,
    // Payments
    canViewFinancials,
    payments,
    paymentsLoading,
    showPaymentModal,
    setShowPaymentModal,
    paymentForm,
    setPaymentForm,
    paymentSaving,
    paymentError,
    setPaymentError,
    handleAddPayment,
  };
}
